import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/CrudPage";
import { FileDown, Printer, Check, Wallet, FileText, AlertTriangle, Clock } from "lucide-react";
import { formatKM, formatDate, deadlineLabel, exportCSV } from "@/lib/format";
import { curMonth } from "@/lib/troskovi";

function SummaryCard({ icon: Icon, label, value, tone }) {
  const tones = {
    primary: "bg-primary text-primary-foreground",
    info: "bg-blue-600 text-white",
    amber: "bg-amber-500 text-white",
    danger: "bg-red-600 text-white",
  };
  return (
    <Card className="p-4 border">
      <div className="flex items-center gap-2">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-4 w-4" /></div>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      </div>
      <div className="text-xl font-extrabold font-heading tabular-nums mt-2">{value}</div>
    </Card>
  );
}

export default function Troskovi() {
  const [month, setMonth] = useState(curMonth());
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("Sve");

  const load = () => api.get("/troskovi", { params: { month } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const markPaid = async (line) => {
    try {
      await api.put(`/invoices/${line.invoice_id}`, { status: "Plaćeno", paid_amount: line.amount });
      toast.success("Faktura označena kao plaćena");
      load();
    } catch (e) {
      toast.error("Greška pri ažuriranju");
    }
  };

  const lines = useMemo(() => {
    const all = data?.lines || [];
    if (filter === "Dug") return all.filter((l) => l.kind === "Faktura" && l.status !== "Plaćeno");
    if (filter === "Fiksni") return all.filter((l) => l.is_fixed);
    if (filter === "Varijabilni") return all.filter((l) => !l.is_fixed);
    if (filter === "POS") return all.filter((l) => l.kind === "POS");
    if (filter === "Odbitni") return all.filter((l) => l.pdv_deductible);
    if (filter === "BezPDV") return all.filter((l) => l.pdv_mode === "Bez PDV-a");
    if (filter === "Prefakturisano") return all.filter((l) => l.reinvoiced);
    return all;
  }, [data, filter]);

  const handleExport = () => {
    const headers = ["Tip", "Naziv / Dobavljač", "Broj računa", "Kategorija", "Bruto (KM)", "Trošak (KM)", "PDV status", "PDV odbitni", "Prefakturisano", "Plaćanje", "Rok plaćanja", "Status"];
    const rows = lines.map((l) => [l.kind, l.name, l.invoice_number || "", l.category, l.amount, l.cost, l.pdv_mode || "", l.pdv_odbitni || "", l.reinvoiced ? "Da" : "", l.payment_method || "", l.due_date || "", l.status || ""]);
    exportCSV(`troskovi_${month}`, headers, rows);
    toast.success("Excel (CSV) preuzet");
  };

  const s = data?.summary;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-heading tracking-tight">Mjesečni troškovi</h1>
          <p className="text-sm text-muted-foreground mt-1">Sve fakture dobavljača za odabrani mjesec · fiksni troškovi su označeni kvačicom</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-full sm:w-44" data-testid="troskovi-month-input" />
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="troskovi-export-excel"><FileDown className="h-4 w-4 mr-1.5" /> Excel</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="troskovi-export-pdf"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
          <SummaryCard icon={Wallet} tone="primary" label="Ukupno trošak" value={formatKM(s.ukupno)} />
          <SummaryCard icon={FileText} tone="info" label="Bruto (sa PDV)" value={formatKM(s.bruto)} />
          <SummaryCard icon={FileText} tone="info" label="PDV za odbitak" value={formatKM(s.pdv_odbitni)} />
          <SummaryCard icon={AlertTriangle} tone="amber" label="Prefakturisano (kupcu)" value={formatKM(s.prefakturisano)} />
          <SummaryCard icon={FileText} tone="info" label="Fiksni" value={formatKM(s.fiksni)} />
          <SummaryCard icon={FileText} tone="info" label="Varijabilni" value={formatKM(s.varijabilni)} />
          <SummaryCard icon={AlertTriangle} tone="amber" label="Ukupan dug" value={formatKM(s.dug)} />
          <SummaryCard icon={AlertTriangle} tone="danger" label="Kasni" value={formatKM(s.kasni)} />
          <SummaryCard icon={Clock} tone="amber" label="Dospijeva ≤5 dana" value={formatKM(s.dospijeva5)} />
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prikaži:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="troskovi-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="Sve">Sve</SelectItem>
            <SelectItem value="Dug">Samo dug (neplaćeno)</SelectItem>
            <SelectItem value="Fiksni">Samo fiksni</SelectItem>
            <SelectItem value="Varijabilni">Samo varijabilni</SelectItem>
            <SelectItem value="POS">Samo POS računi</SelectItem>
            <SelectItem value="Odbitni">PDV odbitni</SelectItem>
            <SelectItem value="BezPDV">Bez PDV-a</SelectItem>
            <SelectItem value="Prefakturisano">Prefakturisano kupcu</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead>Tip</TableHead>
                <TableHead>Naziv / Dobavljač</TableHead>
                <TableHead>Kategorija</TableHead>
                <TableHead className="text-right">Trošak</TableHead>
                <TableHead>Rok plaćanja</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Akcija</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data ? (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">Učitavanje...</TableCell></TableRow>
              ) : lines.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">Nema stavki za ovaj mjesec.</TableCell></TableRow>
              ) : (
                lines.map((l, idx) => (
                  <TableRow key={idx} data-testid="troskovi-row">
                    <TableCell className="whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <Badge tone={l.kind === "POS" ? "info" : l.is_fixed ? "info" : "muted"}>{l.kind === "POS" ? "POS" : l.is_fixed ? "Fiksni" : "Faktura"}</Badge>
                        {l.pdv_mode && l.pdv_mode !== "PDV (nije odbitni)" ? <Badge tone={l.pdv_mode === "Odbitni PDV" ? "success" : "info"}>{l.pdv_mode}</Badge> : null}
                        {l.reinvoiced ? <Badge tone="warning">Prefakturisano</Badge> : null}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium whitespace-nowrap">{l.name}{l.invoice_number ? <span className="text-muted-foreground font-mono text-xs ml-1">#{l.invoice_number}</span> : null}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{l.category}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {formatKM(l.cost)}
                      {l.reinvoiced ? <div className="text-xs text-muted-foreground font-normal">naplaćeno kupcu · bruto {formatKM(l.amount)}</div>
                        : (l.pdv_mode === "Odbitni PDV" ? <div className="text-xs text-muted-foreground font-normal">bruto {formatKM(l.amount)}</div> : null)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {l.kind === "Faktura" ? (
                        l.status === "Plaćeno" ? formatDate(l.due_date) : (
                          <span className="flex items-center gap-2">{formatDate(l.due_date)}<Badge tone={deadlineLabel(l.due_date).tone}>{deadlineLabel(l.due_date).text}</Badge></span>
                        )
                      ) : "—"}
                    </TableCell>
                    <TableCell>{l.kind === "POS" ? <Badge tone={l.payment_method === "Kartica" ? "info" : "muted"}>{l.payment_method}</Badge> : (l.status ? <Badge tone={l.status === "Plaćeno" ? "success" : l.status === "Djelimično" ? "info" : "warning"}>{l.status}</Badge> : "—")}</TableCell>
                    <TableCell className="text-right">
                      {l.kind === "Faktura" && l.status !== "Plaćeno" ? (
                        <Button size="sm" variant="outline" className="h-8" onClick={() => markPaid(l)} data-testid="troskovi-mark-paid"><Check className="h-4 w-4 mr-1" /> Plaćeno</Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
